import { useEffect, useState } from "react";
import { adminApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, ExternalLink, Loader2, Search, XCircle } from "lucide-react";

export function FormField({ label, help, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-[var(--ink-muted)]">{label}</Label>
      <Input {...props} className="h-10" />
      {help && <p className="text-[11px] leading-snug text-[var(--ink-muted)]">{help}</p>}
    </div>
  );
}

export function FormSelect({ label, help, options, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-[var(--ink-muted)]">{label}</Label>
      <select {...props} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      {help && <p className="text-[11px] leading-snug text-[var(--ink-muted)]">{help}</p>}
    </div>
  );
}

export function ChoiceCards({ label, value, onChange, options }) {
  return (
    <fieldset className="space-y-2">
      {label && <legend className="text-xs font-medium text-[var(--ink-muted)]">{label}</legend>}
      <div className="grid gap-2 sm:grid-cols-3">
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button key={option.value} type="button" onClick={() => onChange(option.value)}
              className={`rounded-lg border p-3 text-left transition ${active
                ? "border-[var(--cta)] bg-[var(--cta)]/10 ring-1 ring-[var(--cta)]/30"
                : "border-[var(--glass-border)] bg-white hover:border-[var(--cta)]/50"}`}>
              <span className="block text-sm font-semibold">{option.label}</span>
              {option.help && <span className="mt-1 block text-[11px] leading-tight text-[var(--ink-muted)]">{option.help}</span>}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export function SchemePicker({ label = "Scheme", value, onChange, onSelect,
  flags: { purchase_flag: purchaseFlag, redemption_flag: redemptionFlag, sip_flag: sipFlag,
    stp_flag: stpFlag, swp_flag: swpFlag, switch_flag: switchFlag, amc_code: amcCode } = {} }) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = query.trim();
    if (value || term.length < 2) { setItems([]); return undefined; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await adminApi.get("/schemes", {
          params: { search: term, page_size: 12,
            ...(purchaseFlag == null ? {} : { purchase_flag: purchaseFlag }),
            ...(redemptionFlag == null ? {} : { redemption_flag: redemptionFlag }),
            ...(sipFlag == null ? {} : { sip_flag: sipFlag }),
            ...(stpFlag == null ? {} : { stp_flag: stpFlag }),
            ...(swpFlag == null ? {} : { swp_flag: swpFlag }),
            ...(switchFlag == null ? {} : { switch_flag: switchFlag }),
            ...(amcCode ? { amc_code: amcCode } : {}) },
        });
        setItems(data.items || []);
      } catch (_) { setItems([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, value, purchaseFlag, redemptionFlag, sipFlag, stpFlag, swpFlag, switchFlag, amcCode]);

  const choose = (scheme) => {
    setSelected(scheme);
    setQuery("");
    setItems([]);
    onChange(scheme.scheme_code);
    onSelect?.(scheme);
  };

  if (value) {
    return (
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-[var(--ink-muted)]">{label}</Label>
        <div className="flex min-h-10 items-center justify-between gap-3 rounded-md border border-input bg-white px-3 py-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{selected?.scheme_name || `Scheme ${value}`}</div>
            <div className="text-[11px] text-[var(--ink-muted)]">BSE code {value}</div>
          </div>
          <Button type="button" size="sm" variant="ghost" onClick={() => { onChange(""); onSelect?.(null); setSelected(null); }}>Change</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative space-y-1.5">
      <Label className="text-xs font-medium text-[var(--ink-muted)]">{label}</Label>
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-[var(--ink-muted)]" />
        <Input className="h-10 pl-9" value={query} onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by scheme name or BSE code" />
        {loading && <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-[var(--cta)]" />}
      </div>
      {items.length > 0 && (
        <div className="absolute z-30 max-h-64 w-full overflow-auto rounded-lg border border-[var(--glass-border)] bg-white p-1 shadow-xl">
          {items.map((scheme) => (
            <button type="button" key={scheme.scheme_code} onClick={() => choose(scheme)}
              className="block w-full rounded-md px-3 py-2 text-left hover:bg-[var(--surface-2)]">
              <span className="block text-sm font-medium">{scheme.scheme_name}</span>
              <span className="text-[11px] text-[var(--ink-muted)]">{scheme.scheme_code} · {scheme.amc_name || "BSE scheme"}</span>
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-[var(--ink-muted)]">Only Regular-plan schemes from the synchronized BSE master are shown.</p>
    </div>
  );
}

export function CheckField({ label, checked, onChange, help }) {
  return (
    <label className="flex cursor-pointer gap-3 rounded-lg border border-[var(--glass-border)] bg-white p-3">
      <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[var(--cta)]" checked={checked}
        onChange={(event) => onChange(event.target.checked)} />
      <span><span className="block text-sm font-medium">{label}</span>{help && <span className="block text-[11px] text-[var(--ink-muted)]">{help}</span>}</span>
    </label>
  );
}

export function openBseAction(action) {
  const value = typeof action === "string" ? { href: action, method: "get", parameters: {} } : (action || {});
  const href = value.href || value.url;
  if (!href) return;
  if (String(value.method || "get").toLowerCase() !== "post") {
    window.open(href, "_blank", "noopener,noreferrer");
    return;
  }
  const form = document.createElement("form");
  form.method = "POST"; form.action = href; form.target = "_blank"; form.style.display = "none";
  Object.entries(value.parameters || {}).forEach(([name, fieldValue]) => {
    const input = document.createElement("input"); input.type = "hidden"; input.name = name;
    input.value = fieldValue == null ? "" : String(fieldValue); form.appendChild(input);
  });
  document.body.appendChild(form); form.submit(); form.remove();
}

export function UatResult({ result }) {
  if (!result) return null;
  const bse = result.bse2 || result;
  const status = bse.status || bse.operation_status || result.status || "RECEIVED";
  const success = status === "SUBMITTED";
  const pending = ["SUBMITTING", "UNKNOWN", "WAITING_2FA"].includes(status);
  const authLink = bse.auth_link || bse.auth_url || bse.pg_url;
  const pgAction = bse.pg_action;
  const localReference = result.order_number || result.order_id || result.sip_reg_id || result.xsip_reg_id ||
    result.stp_reg_no || result.swp_reg_no || result.mandate_id || result.payment_ref;
  const exchangeReference = bse.bse_order_id || bse.reg_no || bse.exch_mandate_id || bse.payment_ref_id;
  const Icon = success ? CheckCircle2 : pending ? AlertTriangle : XCircle;
  return (
    <div className={`rounded-xl border p-4 ${success ? "border-emerald-200 bg-emerald-50" : pending ? "border-amber-200 bg-amber-50" : "border-rose-200 bg-rose-50"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Icon className={`h-4 w-4 ${success ? "text-emerald-600" : pending ? "text-amber-600" : "text-rose-600"}`} />
        <span className="text-sm font-semibold">{success ? "Accepted by BSE" : pending ? "BSE confirmation pending" : "Not accepted by BSE"}</span>
        <Badge variant="outline" className="rounded-full bg-white">{status.replaceAll("_", " ")}</Badge>
        {exchangeReference && <span className="font-mono text-xs text-[var(--ink-muted)]">BSE ref {exchangeReference}</span>}
      </div>
      <p className="mt-2 text-sm">{bse.message || result.message || "Request processed."}</p>
      {localReference && <p className="mt-1 text-xs text-[var(--ink-muted)]">Local audit reference {localReference}. This is not a BSE acceptance number.</p>}
      {pgAction ? <Button type="button" size="sm" className="mt-3 rounded-full bg-[var(--cta)]" onClick={() => openBseAction(pgAction)}><ExternalLink className="mr-1 h-3.5 w-3.5" /> Continue on BSE payment page</Button>
        : authLink && <Button asChild size="sm" className="mt-3 rounded-full bg-[var(--cta)]"><a href={authLink} target="_blank" rel="noreferrer"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Continue investor authentication</a></Button>}
    </div>
  );
}

export function CapabilityNotice({ capability, title }) {
  if (!capability) return null;
  const enabled = capability.enabled;
  return <div className={`rounded-xl border p-3 text-sm ${enabled
    ? "border-blue-200 bg-blue-50 text-blue-900"
    : "border-rose-200 bg-rose-50 text-rose-900"}`} data-testid={`capability-${capability.key}`}>
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="outline" className="bg-white">BSE {capability.environment}</Badge>
      <b>{title || capability.label}</b>
      <Badge className={enabled ? "bg-blue-700" : "bg-rose-700"}>{capability.state.replaceAll("_", " ")}</Badge>
    </div>
    <p className="mt-1 text-xs leading-relaxed">{capability.reason}</p>
    {capability.documented && !capability.verified_accepted && <p className="mt-1 text-[11px]">Documented does not mean exchange-verified; each submitted response must be checked for its BSE reference.</p>}
  </div>;
}

export const FREQUENCY_OPTIONS = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "FORTNIGHTLY", label: "Fortnightly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "HALF_YEARLY", label: "Half-yearly" },
  { value: "YEARLY", label: "Yearly" },
];

export const BANK_ACCOUNT_OPTIONS = [
  { value: "SB", label: "Savings account" },
  { value: "CB", label: "Current account" },
  { value: "NE", label: "NRE account" },
  { value: "NO", label: "NRO account" },
];
